import React, { useState } from "react";
import { MapPin, Phone, Mail, Printer, Send, User, MessageSquare, Clock, Navigation, Milestone } from "lucide-react";
import { toast } from "sonner";
import Reveal from "../components/Reveal";
import PageHero from "../components/PageHero";
import { hotel, galleryImages } from "../mock";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast.error("Lütfen adınızı ve mesajınızı giriniz.");
      return;
    }
    toast.success("Mesajınız alındı! En kısa sürede sizinle iletişime geçeceğiz.");
    setForm({ name: "", email: "", phone: "", message: "" });
  };

  const cards = [
    { icon: MapPin, title: "Adres", lines: [hotel.address] },
    { icon: Phone, title: "Telefon", lines: hotel.phones },
    { icon: Printer, title: "Faks", lines: [hotel.fax] },
    { icon: Mail, title: "E-posta", lines: [hotel.email] },
  ];

  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    hotel.address + ", Safranbolu, Karabük"
  )}`;

  const distances = [
    { place: "Tarihi Çarşı (Eski Şehir)", dist: "2 km" },
    { place: "Şehirlerarası Otobüs Terminali", dist: "2 km" },
    { place: "İncekaya Su Kemeri", dist: "8 km" },
    { place: "Bulak (Mencilis) Mağarası", dist: "8 km" },
    { place: "Karabük Şehir Merkezi", dist: "9 km" },
    { place: "Yörük Köyü", dist: "11 km" },
  ];

  return (
    <div className="font-body">
      <PageHero
        image={galleryImages[0]}
        eyebrow="Be Our Guest"
        title="İletişim"
        subtitle="Sorularınız ve rezervasyon talepleriniz için bize ulaşın."
      />

      <section className="py-24 md:py-28 bg-[#fdfbf7]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Info */}
          <Reveal>
            <p className="eyebrow text-[#b8893a] mb-4">Bize Ulaşın</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2b2620] font-500 mb-6">
              Sizi Ağırlamaktan <br /> Mutluluk Duyarız
            </h2>
            <p className="text-[#6b6459] leading-[1.9] text-[15px] mb-9">
              Safranbolu şehir merkezindeki otelimiz; Tarihi Çarşı, İncekaya Su
              Kemeri ve diğer tarihi güzelliklere yakın mesafededir.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {cards.map((c, i) => (
                <div key={i} className="flex items-start gap-4 bg-[#f6f0e6] p-5 rounded-sm">
                  <div className="w-11 h-11 shrink-0 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <c.icon size={19} className="text-[#b8893a]" />
                  </div>
                  <div>
                    <h3 className="text-[13px] uppercase tracking-wide text-[#2b2620] font-600 mb-1">{c.title}</h3>
                    {c.lines.map((l, j) => (
                      <p key={j} className="text-[13px] text-[#6b6459] leading-snug">{l}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 bg-[#2b2620] text-white p-5 rounded-sm mt-5">
              <Clock size={20} className="text-[#d9b877]" />
              <p className="text-[13px] text-white/80">7/24 Resepsiyon &amp; Oda Servisi Hizmeti</p>
            </div>

            {/* Distances & directions */}
            <div className="mt-5 bg-white border border-[#efe7d7] rounded-sm p-6 shadow-[0_18px_50px_-30px_rgba(0,0,0,0.4)]" data-testid="distances-card">
              <div className="flex items-center justify-between gap-4 mb-5">
                <div className="flex items-center gap-2.5">
                  <Milestone size={18} className="text-[#b8893a]" />
                  <h3 className="text-[13px] uppercase tracking-[0.14em] text-[#2b2620] font-600">Merkezlere Uzaklık</h3>
                </div>
                <span className="text-[11px] text-[#9a8f7d]">yaklaşık</span>
              </div>
              <ul className="space-y-3">
                {distances.map((d, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between text-[13px] border-b border-dashed border-[#ece3d1] pb-3 last:border-0 last:pb-0"
                  >
                    <span className="text-[#5b5346] flex items-center gap-2">
                      <MapPin size={14} className="text-[#c9a662]" />
                      {d.place}
                    </span>
                    <span className="font-display text-[#b8893a] text-[17px]">{d.dist}</span>
                  </li>
                ))}
              </ul>
              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="get-directions-btn"
                className="btn-gold w-full py-3.5 rounded-sm flex items-center justify-center gap-2 mt-6"
              >
                <Navigation size={15} /> Yol Tarifi Al
              </a>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={150}>
            <form onSubmit={submit} className="bg-white p-8 rounded-sm shadow-[0_18px_50px_-28px_rgba(0,0,0,0.4)] border border-[#efe7d7]">
              <h3 className="font-display text-2xl text-[#2b2620] font-600 mb-6">Mesaj Gönderin</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-2 border border-[#ddd2bd] px-3 py-3 rounded-sm focus-within:border-[#b8893a] transition-colors">
                  <User size={16} className="text-[#b8893a]" />
                  <input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Ad Soyad" className="w-full outline-none bg-transparent text-[14px] text-[#2b2620]" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 border border-[#ddd2bd] px-3 py-3 rounded-sm focus-within:border-[#b8893a] transition-colors">
                    <Mail size={16} className="text-[#b8893a]" />
                    <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="E-posta" className="w-full outline-none bg-transparent text-[14px] text-[#2b2620]" />
                  </div>
                  <div className="flex items-center gap-2 border border-[#ddd2bd] px-3 py-3 rounded-sm focus-within:border-[#b8893a] transition-colors">
                    <Phone size={16} className="text-[#b8893a]" />
                    <input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="Telefon" className="w-full outline-none bg-transparent text-[14px] text-[#2b2620]" />
                  </div>
                </div>
                <div className="flex items-start gap-2 border border-[#ddd2bd] px-3 py-3 rounded-sm focus-within:border-[#b8893a] transition-colors">
                  <MessageSquare size={16} className="text-[#b8893a] mt-1" />
                  <textarea value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Mesajınız" rows={5} className="w-full outline-none bg-transparent text-[14px] text-[#2b2620] resize-none" />
                </div>
                <button type="submit" className="btn-gold w-full py-3.5 rounded-sm flex items-center justify-center gap-2">
                  <Send size={15} /> Gönder
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>

      {/* Map */}
      <section className="relative h-[440px] w-full">
        <iframe
          title="Safranbolu Çelebi Otel Konum"
          src={hotel.mapEmbed}
          className="w-full h-full border-0 grayscale-[0.2]"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
        <a
          href={directionsUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="map-directions-btn"
          className="absolute right-4 bottom-4 md:right-8 md:bottom-8 btn-gold px-5 py-3 rounded-sm flex items-center gap-2 shadow-[0_16px_40px_-16px_rgba(0,0,0,0.7)]"
        >
          <Navigation size={15} /> Yol Tarifi Al
        </a>
      </section>
    </div>
  );
};

export default Contact;
